import type { Drill } from "../../domain/learning/model/drill";
import type { DrillGoal } from "../../domain/learning/model/drill-goal";
import type { Level } from "../../domain/learning/model/level";
import type { LevelSummary } from "../../domain/learning/model/level-summary";
import type { Chapter } from "../../domain/story/model/chapter";
import type { LevelStory } from "../../domain/story/model/level-story";
import type { Keystroke } from "../../domain/vim/model/keystroke";
import type { LevelFileData, ManifestData, StoryFileData } from "./schema";

/**
 * 「検証済み外部データ → ドメインの値オブジェクト」への変換（腐敗防止層。ADR-006）。
 * Zodスキーマの型は構造的にドメイン型とほぼ一致するが、ドメイン層がzodへ依存しない
 * ようにするため、この層で明示的に変換する。
 *
 * `exactOptionalPropertyTypes: true` の都合上、zodの`.optional()`が生む
 * `T | undefined` をそのままスプレッドせず、値がある場合のみキーを持たせる。
 */

type LevelFileDrill = LevelFileData["lessons"][number]["drills"][number];

function toDrillGoal(data: LevelFileDrill["goal"]): DrillGoal {
  return {
    ...(data.buffer !== undefined ? { buffer: data.buffer } : {}),
    ...(data.cursor !== undefined ? { cursor: data.cursor } : {}),
    ...(data.requireMode !== undefined ? { requireMode: data.requireMode } : {}),
    ...(data.requiredCommands !== undefined ? { requiredCommands: data.requiredCommands } : {}),
  };
}

function toKeystroke(data: { key: string; ctrl?: boolean | undefined }): Keystroke {
  return data.ctrl !== undefined ? { key: data.key, ctrl: data.ctrl } : { key: data.key };
}

function toDrill(data: LevelFileDrill): Drill {
  return {
    id: data.id,
    instruction: data.instruction,
    initialBuffer: data.initialBuffer,
    initialCursor: data.initialCursor,
    goal: toDrillGoal(data.goal),
    keystrokeLimit: data.keystrokeLimit,
    hints: data.hints,
    ...(data.solution !== undefined ? { solution: data.solution.map(toKeystroke) } : {}),
  };
}

export function toManifest(data: ManifestData): readonly LevelSummary[] {
  return data.levels.map((entry) => ({
    id: entry.id,
    title: entry.title,
    summary: entry.summary,
    commands: entry.commands,
    drillCount: entry.drillCount,
  }));
}

export function toLevel(data: LevelFileData): Level {
  return {
    id: data.id,
    title: data.title,
    summary: data.summary,
    commands: data.commands,
    lessons: data.lessons.map((lesson) => ({
      id: lesson.id,
      title: lesson.title,
      drills: lesson.drills.map(toDrill),
    })),
    exam: { id: data.exam.id, drills: data.exam.drills.map(toDrill) },
  };
}

function toChapter(data: StoryFileData["intro"]): Chapter {
  return {
    id: data.id,
    levelId: data.levelId,
    kind: data.kind,
    scenes: data.scenes,
  };
}

export function toLevelStory(data: StoryFileData): LevelStory {
  return {
    levelId: data.levelId,
    intro: toChapter(data.intro),
    outro: toChapter(data.outro),
    companionReactions: data.companionReactions,
  };
}
